// Service for tracking recently visited items (stored in localStorage)
// Data is isolated per user to prevent cross-tenant data leakage

const BASE_STORAGE_KEY = 'recently_visited';
const MAX_ITEMS = 5;

// Get the current user ID from localStorage for data isolation
const getUserId = () => {
  try {
    // Check 'user' key first (AuthContext stores user data here)
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.id || parsed.user_id || null;
    }
    // Fallback: check 'auth' key
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.user?.id || parsed.userId || null;
    }
  } catch {
    return null;
  }
  return null;
};

// Get tenant-specific storage key
const getStorageKey = () => {
  const userId = getUserId();
  if (userId) {
    return `${BASE_STORAGE_KEY}_${userId}`;
  }
  return BASE_STORAGE_KEY;
};

const recentlyVisitedService = {
  // Get all recently visited items for current user
  getAll() {
    try {
      const key = getStorageKey();
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  // Add a new item to recently visited
  add(item) {
    try {
      const key = getStorageKey();
      const items = this.getAll();
      
      // Remove duplicate if exists
      const filtered = items.filter(i => !(i.type === item.type && i.id === item.id));
      
      // Add new item at the beginning
      filtered.unshift({
        ...item,
        visitedAt: new Date().toISOString()
      });
      
      // Keep only MAX_ITEMS
      const trimmed = filtered.slice(0, MAX_ITEMS);
      
      localStorage.setItem(key, JSON.stringify(trimmed));
      return trimmed;
    } catch (error) {
      console.error('Error adding to recently visited:', error);
      return [];
    }
  },

  // Clear all recently visited for current user
  clear() {
    const key = getStorageKey();
    localStorage.removeItem(key);
  },
  
  // Clear old data from previous implementation (one-time cleanup)
  clearLegacyData() {
    localStorage.removeItem(BASE_STORAGE_KEY);
  },

  // Track a project visit
  trackProject(project) {
    return this.add({
      type: 'project',
      id: project.id,
      name: project.name,
      icon: 'FolderKanban',
      link: `/projects/${project.id}`,
      meta: project.project_number || ''
    });
  },

  // Track an address visit
  trackAddress(address) {
    return this.add({
      type: 'address',
      id: address.id,
      name: address.company_name || `${address.first_name} ${address.last_name}`,
      icon: 'Users',
      link: `/addresses/${address.id}`,
      meta: address.city || ''
    });
  },

  // Track a task visit
  trackTask(task) {
    return this.add({
      type: 'task',
      id: task.id,
      name: task.title,
      icon: 'CheckSquare',
      link: `/tasks/${task.id}`,
      meta: task.due_date ? new Date(task.due_date).toLocaleDateString('de-DE') : ''
    });
  }
};

export default recentlyVisitedService;
